import { getValidTenantIds } from "./tenant-access";

export type JwtClaims = Record<string, unknown>;

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded =
    padding === 0
      ? normalized
      : normalized.padEnd(normalized.length + (4 - padding), "=");

  return atob(padded);
}

function toStringArray(value: unknown) {
  if (typeof value === "string") {
    const trimmed = value.trim();

    return trimmed ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
}

function matchesClaimKey(key: string, candidates: string[]) {
  const normalizedKey = key.toLowerCase();

  return candidates.some((candidate) => {
    const normalizedCandidate = candidate.toLowerCase();

    return (
      normalizedKey === normalizedCandidate ||
      normalizedKey.endsWith(`/${normalizedCandidate}`) ||
      normalizedKey.endsWith(`:${normalizedCandidate}`) ||
      normalizedKey.endsWith(`.${normalizedCandidate}`) ||
      normalizedKey.endsWith(normalizedCandidate)
    );
  });
}

export function decodeJwtClaims(token: string): JwtClaims {
  const [, payload] = token.split(".");

  if (!payload) {
    throw new Error("The access token is missing a JWT payload.");
  }

  return JSON.parse(decodeBase64Url(payload)) as JwtClaims;
}

export function extractTenantIds(claims: JwtClaims) {
  const directPracticeIds = toStringArray(claims.practiceId);
  const validDirectPracticeIds = getValidTenantIds(directPracticeIds);

  if (validDirectPracticeIds.length > 0) {
    return validDirectPracticeIds;
  }

  const alternatePracticeIds = toStringArray(claims.practiceIds);
  const validAlternatePracticeIds = getValidTenantIds(alternatePracticeIds);

  if (validAlternatePracticeIds.length > 0) {
    return validAlternatePracticeIds;
  }

  const directClinicIds = toStringArray(claims.clinicId);
  const validDirectClinicIds = getValidTenantIds(directClinicIds);

  if (validDirectClinicIds.length > 0) {
    return validDirectClinicIds;
  }

  const alternateClinicIds = [
    ...toStringArray(claims.clinicIds),
    ...toStringArray(claims.clinic_id),
    ...toStringArray(claims.clinic_ids),
  ];
  const validAlternateClinicIds = getValidTenantIds(alternateClinicIds);

  if (validAlternateClinicIds.length > 0) {
    return validAlternateClinicIds;
  }

  const legacyTenantIds = toStringArray(claims.tenantId);
  const validLegacyTenantIds = getValidTenantIds(legacyTenantIds);

  if (validLegacyTenantIds.length > 0) {
    return validLegacyTenantIds;
  }

  const alternateLegacyTenantIds = toStringArray(claims.tenantIds);
  const validAlternateLegacyTenantIds = getValidTenantIds(
    alternateLegacyTenantIds,
  );

  if (validAlternateLegacyTenantIds.length > 0) {
    return validAlternateLegacyTenantIds;
  }

  for (const [key, value] of Object.entries(claims)) {
    if (
      matchesClaimKey(key, [
        "practiceId",
        "practiceIds",
        "practice_ids",
        "clinicId",
        "clinicIds",
        "clinic_id",
        "tenantId",
        "tenantIds",
        "clinic_ids",
      ])
    ) {
      const ids = toStringArray(value);
      const validIds = getValidTenantIds(ids);

      if (validIds.length > 0) {
        return validIds;
      }
    }
  }

  return [];
}

export function extractRoles(claims: JwtClaims) {
  const directRoles = toStringArray(claims.roles);

  if (directRoles.length > 0) {
    return [...new Set(directRoles)];
  }

  for (const [key, value] of Object.entries(claims)) {
    if (matchesClaimKey(key, ["roles", "role"])) {
      const roles = toStringArray(value);

      if (roles.length > 0) {
        return [...new Set(roles)];
      }
    }
  }

  return [];
}
